import React from 'react';
import styled from '@emotion/styled';
import { useLeaflet } from 'react-leaflet';
import { useStoreState } from 'easy-peasy';
import useDimensions from 'react-use-dimensions';

import { numberFormat, isMobile } from '~/core/scripts/utils';
import { clamp } from '~/core/scripts/chart-components/utils';
import { getMoveAngle } from '~/scripts/utils';

import * as S from './Tooltip.styles';

const TooltipHeight = 103;

const getPopupPosition = (angle, size, dir, ttWidth) => {
  const distance = Math.min(size.x, size.y) / 2;
  const x = Math.cos(angle) * distance * dir;
  const y = Math.sin(angle) * distance * dir;
  const targetX = clamp(size.x / 2 + x, 15, size.x - ttWidth - 15);
  const targetY = clamp(size.y / 2 + y, 15, size.y - TooltipHeight - 15);

  return [targetX, targetY];
};

function getXPos(angle, size, dir, ttWidth) {
  const distance = Math.min(size.x, size.y) / 2;
  const x = Math.cos(angle) * distance * dir;
  const targetX = clamp(size.x / 2 + x, 15, size.x - ttWidth - 15);

  return targetX;
}

function getYPos(angle, size, dir) {
  const distance = Math.min(size.x, size.y) / 2;
  const y = Math.sin(angle) * distance * dir;
  const targetY = clamp(size.y / 2 + y, 15, size.y - TooltipHeight - 15);

  return targetY;
}

function fixOverlap(positions) {
  if (typeof window === 'undefined' || window.innerHeight > 768) {
    return positions;
  }

  let [y1, y2] = positions;

  if (Math.abs(y1 - y2) < TooltipHeight) {
    y1 = Math.max(0, y2 - (TooltipHeight - 15));
  }

  return [y1, y2];
}

const Tooltip = ({ indicatorConfig, data, map, angle, i, y }) => {
  const {
    shortLabel,
    label,
    calculateValue,
    unit = '',
    decimals = 0,
  } = indicatorConfig;
  const [ref, { width }] = useDimensions();

  const entries = data.geojson.features.map((feat) => feat.properties);
  const value = calculateValue(entries);
  const colorScale = useStoreState((state) => state.app.colorScale);
  const tooltipIsVisible = useStoreState((state) => state.tooltip.isVisible);

  const tooltipLabel = shortLabel || label;
  const tooltipValue = numberFormat(value, decimals);
  const x = getXPos(angle, map.getSize(), data.moveDir, width);

  return (
    <S.TooltipWrapper
      isVisible
      tooltipIsVisible={tooltipIsVisible}
      ref={ref}
      style={{
        top: y,
        left: x,
        // width: TooltipWidth,
        // height: TooltipHeight,
        borderLeftColor: colorScale(value),
      }}
    >
      <S.TooltipHeader minimizeSize>
        <S.TooltipTitle
          minimizeSize
          dangerouslySetInnerHTML={{
            __html: `${tooltipLabel} im ${i + 1}. Teil`,
          }}
        />
      </S.TooltipHeader>
      <S.TooltipBody minimizeSize>
        <S.TooltipValue minimizeSize>{tooltipValue}</S.TooltipValue>
        <S.TooltipUnit minimizeSize>{unit}</S.TooltipUnit>
      </S.TooltipBody>
    </S.TooltipWrapper>
  );
};

const ValueDisplay = ({ data }) => {
  const { map } = useLeaflet();
  const selectedIndicator = useStoreState(
    (state) => state.app.selectedIndicator
  );

  if (!data) {
    return null;
  }

  const angle = getMoveAngle(data.shapes, map);

  const yPositions = data.shapes.map((shape) =>
    getYPos(angle, map.getSize(), shape.moveDir)
  );
  const fixedYPositions = fixOverlap(yPositions);

  return data.shapes.map((shape, i) => (
    <Tooltip
      key={`tooltip-${i}`}
      i={i}
      data={shape}
      map={map}
      angle={angle}
      indicatorConfig={selectedIndicator}
      y={fixedYPositions[i]}
    />
  ));
};

export default ValueDisplay;
